import { useState, useEffect } from "react";
import api from "@/lib/api";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/marketing/Navbar";
import { Button } from "@/components/ui/button";
import {
  Target, LayoutDashboard, LineChart, GitCompare, FileBarChart,
  Receipt, Users2, Settings as SettingsIcon, CreditCard, ShieldCheck, LogOut,
  Menu, X, PlusCircle, Plug, Timer, TrendingUp, BookMarked, Rocket, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TrialPill } from "@/components/app/TrialPill";

const COST_ROLES = ["owner", "admin", "finance"];

function navItems(user, setupComplete) {
  const canCost = user?.global_admin || COST_ROLES.includes(user?.role);
  const isAdminish = user?.global_admin || user?.role === "owner" || user?.role === "admin";
  const items = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/time-tracker", label: "Time Tracker", icon: Timer },
    { to: "/app/forecasts", label: "Forecasting", icon: LineChart },
    { to: "/app/decision-centre", label: "Decisions", icon: Target },
    { to: "/app/scenarios", label: "Scenarios", icon: GitCompare },
    { to: "/app/reports", label: "Reports", icon: FileBarChart },
  ];
  if (canCost) {
    items.push({ to: "/app/value", label: "Costs & ROI", icon: TrendingUp });
    items.push({ to: "/app/ai-tools", label: "AI Tools", icon: Sparkles });
    items.push({ to: "/app/evidence", label: "Evidence Quality", icon: BookMarked });
    items.push({ to: "/app/pnl", label: "P&L Impact", icon: Receipt });
    items.push({ to: "/app/staff-costs", label: "Staff Costs", icon: Users2 });
  }
  if (isAdminish) {
    items.push({ to: "/app/integrations", label: "Integrations", icon: Plug });
  }
  if (!setupComplete) items.push({ to: "/app/onboarding", label: "Setup checklist", icon: Rocket });
  items.push({ to: "/app/settings", label: "Settings", icon: SettingsIcon });
  items.push({ to: "/app/billing", label: "Billing", icon: CreditCard });
  if (user?.global_admin) items.push({ to: "/admin", label: "Admin", icon: ShieldCheck });
  return items;
}

export default function AppLayout() {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [setupComplete, setSetupComplete] = useState(true);
  useEffect(() => {
    if (!user) return;
    api.get("/setup/progress").then(({ data }) => setSetupComplete(!!data.complete)).catch(() => setSetupComplete(false));
  }, [user]);
  const items = navItems(user, setupComplete);

  const SidebarBody = () => (
    <>
      <div className="px-5 py-5">
        <Logo />
      </div>
      <div className="px-3">
        <Button className="w-full justify-start bg-editorial hover:bg-editorial/90" onClick={() => { setOpen(false); navigate("/app/time-tracker"); }} data-testid="sidebar-new-entry">
          <PlusCircle className="mr-2 h-4 w-4" /> Add Time
        </Button>
      </div>
      <nav className="mt-5 flex-1 space-y-1 px-3">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive ? "bg-editorial text-white" : "text-slate-600 hover:bg-slate-100"
              )
            }
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
          >
            <it.icon className="h-4 w-4" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <p className="mb-2 px-2 text-[11px] leading-snug text-slate-400">
          NetLift is for AI rollout and investment decisions. It is not an employee monitoring tool.
        </p>
        <div className="mb-2 px-2">
          <p className="truncate text-sm font-medium text-slate-700">{user?.name}</p>
          <p className="truncate text-xs text-slate-400">{workspace?.name} · {user?.role}</p>
        </div>
        <Button variant="ghost" className="w-full justify-start text-slate-500" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <SidebarBody />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-white">
            <button className="absolute right-3 top-4" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarBody />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-5 lg:hidden">
          <Logo />
          <button onClick={() => setOpen(true)} data-testid="open-sidebar"><Menu className="h-6 w-6" /></button>
        </header>
        <main className="flex-1 overflow-x-hidden px-5 py-6 sm:px-8">
          <TrialPill />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
